import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, formatMoney, formatDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { CATEGORY_COLORS, CATEGORY_HEX_ARRAY, KPI_ACCENTS, avatarColor } from "@/lib/colors";
import { Kicker, LoadingCard, ErrorCard, EmptyState, StatusPill } from "@/components/common";
import AIInsights from "@/components/AIInsights";
import {
  ArrowUpRight,
  Plus,
  TrendingUp,
  Users,
  Clock,
  CheckCircle2,
  BarChart3,
  Wallet,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

function StatCard({ kicker, value, hint, icon: Icon, accent = "indigo", testId }) {
  const a = KPI_ACCENTS[accent] || KPI_ACCENTS.indigo;
  return (
    <div
      data-testid={testId}
      className="relative rounded-xl border border-zinc-200 bg-white p-5 card-hover overflow-hidden"
    >
      <div
        aria-hidden
        className={`absolute left-0 top-0 bottom-0 w-[3px] ${a.bar} opacity-90`}
      />
      <div className="flex items-center justify-between">
        <Kicker>{kicker}</Kicker>
        {Icon ? (
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${a.iconBg}`}>
            <Icon className={`w-4 h-4 ${a.iconText}`} />
          </div>
        ) : null}
      </div>
      <div className="mt-3 font-display text-[28px] font-semibold tracking-tight text-zinc-950 leading-none">
        {value}
      </div>
      {hint ? <div className="mt-2 text-xs text-zinc-500">{hint}</div> : null}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const isManager = user?.role === "manager";
  const [summary, setSummary] = useState(null);
  const [trend, setTrend] = useState([]);
  const [breakdown, setBreakdown] = useState({ by_category: [], by_employee: [], by_customer: [] });
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rankTab, setRankTab] = useState("employee");
  const [expandedEmp, setExpandedEmp] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const calls = [
        api.get("/analytics/summary"),
        api.get("/analytics/trend"),
        api.get("/analytics/breakdown"),
      ];
      if (isManager) calls.push(api.get("/analytics/employee-details"));
      const results = await Promise.all(calls);
      setSummary(results[0].data);
      setTrend(results[1].data.trend || []);
      setBreakdown(results[2].data);
      if (isManager) setEmployees(results[3].data.employees || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const donutData = useMemo(
    () =>
      (breakdown?.by_category || []).map((c) => ({
        name: c.name,
        value: Number(c.total || 0),
      })),
    [breakdown]
  );

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-6 py-8">
        <LoadingCard />
      </div>
    );
  }
  if (error) {
    return (
      <div className="max-w-6xl mx-auto px-6 py-8">
        <ErrorCard message={error} onRetry={load} />
      </div>
    );
  }

  const totals = summary?.totals_month || { CAD: 0, USD: 0 };
  const counts = summary?.counts || {};

  return (
    <div className="max-w-6xl mx-auto px-6 py-8 animate-fade-up">
      <div className="flex items-end justify-between mb-8">
        <div>
          <Kicker>Welcome, {user?.name?.split(" ")[0] || "there"}</Kicker>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-zinc-950 mt-1">
            {isManager ? "Team expenses overview" : "Your expense snapshot"}
          </h1>
        </div>
        <Link
          to="/add"
          data-testid="dashboard-new-expense-cta"
          className="inline-flex items-center gap-2 rounded-lg bg-zinc-950 text-white px-4 py-2.5 text-sm font-medium hover:bg-zinc-800 transition-colors"
        >
          <Plus className="w-4 h-4" /> Submit a new expense
        </Link>
      </div>

      {/* KPI Row — each card gets its own accent color */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          kicker="This month · CAD"
          value={formatMoney(totals.CAD, "CAD")}
          hint={`${counts.total || 0} expenses total`}
          icon={Wallet}
          accent="indigo"
          testId="kpi-month-cad"
        />
        <StatCard
          kicker="This month · USD"
          value={formatMoney(totals.USD, "USD")}
          hint="Converted from receipts"
          icon={TrendingUp}
          accent="emerald"
          testId="kpi-month-usd"
        />
        <StatCard
          kicker={isManager ? "Pending approvals" : "Awaiting review"}
          value={counts.pending || 0}
          hint={`${counts.approved || 0} approved · ${counts.rejected || 0} rejected`}
          icon={Clock}
          accent="amber"
          testId="kpi-pending"
        />
        {isManager ? (
          <StatCard
            kicker="Active employees"
            value={summary?.active_employees || 0}
            hint="Submitted at least one expense"
            icon={Users}
            accent="violet"
            testId="kpi-employees"
          />
        ) : (
          <StatCard
            kicker="Approved"
            value={counts.approved || 0}
            hint={`${counts.rejected || 0} rejected`}
            icon={CheckCircle2}
            accent="emerald"
            testId="kpi-approved"
          />
        )}
      </div>

      {/* Manager charts */}
      {isManager ? (
        <div className="mt-4">
          <AIInsights />
        </div>
      ) : null}

      {isManager ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
          <div data-testid="chart-trend" className="lg:col-span-2 rounded-xl border border-zinc-200 bg-white p-6">
            <Kicker>6-month trend</Kicker>
            <h3 className="font-display text-lg font-medium text-zinc-950 mt-1 mb-4">Spend by month</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trend} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
                  <defs>
                    <linearGradient id="barCad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#6366f1" stopOpacity={1} />
                      <stop offset="100%" stopColor="#6366f1" stopOpacity={0.6} />
                    </linearGradient>
                    <linearGradient id="barUsd" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10b981" stopOpacity={1} />
                      <stop offset="100%" stopColor="#10b981" stopOpacity={0.6} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" vertical={false} />
                  <XAxis dataKey="label" stroke="#a1a1aa" fontSize={12} tickLine={false} axisLine={false} />
                  <YAxis stroke="#a1a1aa" fontSize={12} tickLine={false} axisLine={false} />
                  <Tooltip
                    contentStyle={{ borderRadius: 8, border: "1px solid #e4e4e7", fontSize: 12 }}
                    cursor={{ fill: "#fafafa" }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" />
                  <Bar dataKey="CAD" fill="url(#barCad)" radius={[6, 6, 0, 0]} maxBarSize={38} />
                  <Bar dataKey="USD" fill="url(#barUsd)" radius={[6, 6, 0, 0]} maxBarSize={38} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div data-testid="chart-category" className="rounded-xl border border-zinc-200 bg-white p-6">
            <Kicker>Category mix</Kicker>
            <h3 className="font-display text-lg font-medium text-zinc-950 mt-1 mb-4">By category</h3>
            {donutData.length === 0 ? (
              <div className="text-sm text-zinc-500 h-48 flex items-center justify-center">No data yet</div>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={donutData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                    >
                      {donutData.map((entry, i) => {
                        const hex = CATEGORY_COLORS[entry.name]?.hex || CATEGORY_HEX_ARRAY[i % CATEGORY_HEX_ARRAY.length];
                        return <Cell key={i} fill={hex} />;
                      })}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e4e4e7", fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
      ) : null}

      {/* Rankings (manager) */}
      {isManager ? (
        <div className="mt-4 rounded-xl border border-zinc-200 bg-white p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <Kicker>Rankings</Kicker>
              <h3 className="font-display text-lg font-medium text-zinc-950 mt-1">Top spenders</h3>
            </div>
            <div className="inline-flex rounded-lg border border-zinc-200 bg-zinc-50 p-1 text-sm">
              {[
                { id: "employee", label: "By employee" },
                { id: "customer", label: "By customer" },
              ].map((t) => (
                <button
                  key={t.id}
                  data-testid={`rank-tab-${t.id}`}
                  onClick={() => setRankTab(t.id)}
                  className={`px-3 py-1 rounded-md transition-colors ${
                    rankTab === t.id ? "bg-white text-zinc-950 shadow-sm" : "text-zinc-500 hover:text-zinc-800"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <RankingTable
            rows={rankTab === "employee" ? breakdown.by_employee : breakdown.by_customer}
            colored={rankTab === "employee"}
          />
        </div>
      ) : null}

      {/* Per-employee breakdown (manager) */}
      {isManager ? (
        <div className="mt-4 rounded-xl border border-zinc-200 bg-white">
          <div className="p-6 border-b border-zinc-100 flex items-center justify-between">
            <div>
              <Kicker>Employee breakdown</Kicker>
              <h3 className="font-display text-lg font-medium text-zinc-950 mt-1">Per-employee detail</h3>
              <p className="text-xs text-zinc-500 mt-1">
                Click a row to see their category split and approval status.
              </p>
            </div>
            <BarChart3 className="w-5 h-5 text-zinc-400" />
          </div>

          {employees.length === 0 ? (
            <div className="p-6">
              <EmptyState title="No employee activity yet" description="Once employees submit expenses, their breakdowns appear here." />
            </div>
          ) : (
            <ul>
              {employees.map((emp) => (
                <EmployeeRow
                  key={emp.user_id}
                  emp={emp}
                  expanded={expandedEmp === emp.user_id}
                  onToggle={() => setExpandedEmp(expandedEmp === emp.user_id ? null : emp.user_id)}
                />
              ))}
            </ul>
          )}
        </div>
      ) : null}

      {/* Recent activity */}
      <div className="mt-4 rounded-xl border border-zinc-200 bg-white">
        <div className="p-6 border-b border-zinc-100 flex items-center justify-between">
          <div>
            <Kicker>Recent activity</Kicker>
            <h3 className="font-display text-lg font-medium text-zinc-950 mt-1">
              {isManager ? "Latest submissions" : "Your latest expenses"}
            </h3>
          </div>
          <Link
            to="/expenses"
            data-testid="dashboard-view-all-link"
            className="text-sm text-zinc-600 hover:text-zinc-950 inline-flex items-center gap-1"
          >
            View all <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        {summary?.recent?.length ? (
          <ul>
            {summary.recent.map((e) => {
              const c = CATEGORY_COLORS[e.category] || CATEGORY_COLORS.Other;
              return (
                <li
                  key={e.id}
                  className="px-6 py-4 border-b border-zinc-100 last:border-b-0 flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <span
                      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-medium ${c.bg} ${c.text} ${c.border} border`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
                      {e.category}
                    </span>
                    <div>
                      <Link
                        to={`/expense/${e.id}`}
                        data-testid={`recent-row-${e.id}`}
                        className="text-sm font-medium text-zinc-950 hover:underline"
                      >
                        {e.merchant || e.category} · {formatMoney(e.amount, e.currency)}
                      </Link>
                      <div className="text-xs text-zinc-500 mt-0.5">
                        {isManager ? `${e.user_name} · ` : ""}
                        {formatDate(e.date)}
                        {e.customer ? ` · ${e.customer}` : ""}
                      </div>
                    </div>
                  </div>
                  <StatusPill status={e.status} />
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="p-6">
            <EmptyState
              title="No expenses yet"
              description="Submit your first expense to see it here."
              action={
                <Link
                  to="/add"
                  data-testid="empty-new-expense-cta"
                  className="inline-flex items-center gap-2 rounded-lg bg-zinc-950 text-white px-4 py-2 text-sm font-medium hover:bg-zinc-800"
                >
                  <Plus className="w-4 h-4" /> New expense
                </Link>
              }
            />
          </div>
        )}
      </div>
    </div>
  );
}

function RankingTable({ rows, colored = false }) {
  if (!rows || rows.length === 0) {
    return <div className="text-sm text-zinc-500 py-6 text-center">No ranking data yet</div>;
  }
  const top = rows.slice(0, 6);
  const max = Math.max(...top.map((r) => r.total || 0), 1);
  const palette = ["bg-indigo-500", "bg-emerald-500", "bg-amber-500", "bg-rose-500", "bg-sky-500", "bg-violet-500"];
  return (
    <div className="space-y-3">
      {top.map((r, idx) => {
        const barClass = colored ? (palette[idx % palette.length]) : "bg-zinc-900";
        return (
          <div key={r.name} data-testid={`rank-row-${r.name}`} className="flex items-center gap-4">
            <div className="w-40 truncate text-sm text-zinc-700">{r.name}</div>
            <div className="flex-1 h-2 rounded-full bg-zinc-100 overflow-hidden">
              <div className={`h-full ${barClass} transition-[width]`} style={{ width: `${Math.max(4, (r.total / max) * 100)}%` }} />
            </div>
            <div className="w-40 text-right text-sm text-zinc-900 font-medium tabular-nums">
              {formatMoney(r.CAD, "CAD")}
              {r.USD > 0 ? <span className="text-zinc-400"> · {formatMoney(r.USD, "USD")}</span> : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function EmployeeRow({ emp, expanded, onToggle }) {
  const initials = (emp.user_name || "?")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  const totalForBar = emp.categories.reduce((s, c) => s + (c.total || 0), 0) || 1;
  const avatar = avatarColor(emp.user_id);
  return (
    <li data-testid={`emp-row-${emp.user_id}`} className="border-b border-zinc-100 last:border-b-0">
      <button
        onClick={onToggle}
        data-testid={`emp-toggle-${emp.user_id}`}
        className="w-full px-6 py-4 flex items-center gap-4 hover:bg-zinc-50 transition-colors text-left"
      >
        <div className={`w-9 h-9 rounded-full ${avatar} text-white flex items-center justify-center text-xs font-semibold`}>
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-zinc-950 truncate">{emp.user_name}</div>
          <div className="text-xs text-zinc-500 truncate">{emp.user_email}</div>
        </div>

        {/* Stacked category bar */}
        <div className="hidden md:flex items-center gap-2 w-48">
          <div className="flex-1 h-2 rounded-full overflow-hidden bg-zinc-100 flex">
            {emp.categories.map((c) => {
              const pct = ((c.total || 0) / totalForBar) * 100;
              const color = CATEGORY_COLORS[c.name]?.hex || "#a1a1aa";
              return <div key={c.name} style={{ width: `${pct}%`, backgroundColor: color }} title={`${c.name}: ${c.total}`} />;
            })}
          </div>
        </div>

        {/* Status dots */}
        <div className="hidden lg:flex items-center gap-2 text-[11px]">
          <span className="inline-flex items-center gap-1 text-amber-700">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> {emp.status.pending}
          </span>
          <span className="inline-flex items-center gap-1 text-emerald-700">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> {emp.status.approved}
          </span>
          <span className="inline-flex items-center gap-1 text-red-700">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500" /> {emp.status.rejected}
          </span>
        </div>

        <div className="text-right min-w-[140px]">
          <div className="text-sm font-medium text-zinc-950 tabular-nums">
            {formatMoney(emp.totals.CAD, "CAD")}
          </div>
          {emp.totals.USD > 0 ? (
            <div className="text-xs text-zinc-500 tabular-nums">{formatMoney(emp.totals.USD, "USD")}</div>
          ) : null}
        </div>

        {expanded ? (
          <ChevronUp className="w-4 h-4 text-zinc-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-zinc-400" />
        )}
      </button>

      {expanded ? (
        <div data-testid={`emp-detail-${emp.user_id}`} className="px-6 pb-5 animate-fade-up">
          <div className="rounded-lg border border-zinc-100 bg-zinc-50 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="label-kicker">Category split</div>
              <div className="text-xs text-zinc-500">{emp.count} expenses</div>
            </div>
            {emp.categories.length === 0 ? (
              <div className="text-sm text-zinc-500">No categories yet.</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {emp.categories.map((c) => {
                  const color = CATEGORY_COLORS[c.name] || CATEGORY_COLORS.Other;
                  return (
                    <div
                      key={c.name}
                      data-testid={`emp-cat-${emp.user_id}-${c.name}`}
                      className="flex items-center justify-between rounded-md bg-white border border-zinc-100 px-3 py-2"
                    >
                      <span className={`inline-flex items-center gap-2 text-sm ${color.text}`}>
                        <span className={`w-2 h-2 rounded-full ${color.dot}`} />
                        {c.name}
                        <span className="text-[10px] text-zinc-400 ml-1">({c.count})</span>
                      </span>
                      <span className="text-sm text-zinc-900 tabular-nums font-medium">
                        {c.CAD > 0 ? formatMoney(c.CAD, "CAD") : null}
                        {c.CAD > 0 && c.USD > 0 ? <span className="text-zinc-300 mx-1">·</span> : null}
                        {c.USD > 0 ? formatMoney(c.USD, "USD") : null}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      ) : null}
    </li>
  );
}
